import { QueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';

import withLazyLoading from '../../hoc/withLazyLoading';
import { ACCOUNT_ROUTES } from '../../navigation/paths';
import LogbookTemplates from '../../pages/logBooks/components/LogbookTemplates';
import { logbookTemplateLoader } from '../../pages/logBooks/hooks/useLogbookTemplateTable';
import { logbookConfigurationLoader, LogbookConfigurationPage } from '../../pages/logbookConfiguration';
import LogbookList from '../../pages/logbookConfiguration/components/LogbookList';
import { logbookListLoader } from '../../pages/logbookConfiguration/hooks/useLogbookListTable';
import { OcrDataInputPage } from '../../pages/ocrDataInput';
import { createCrumbHandle, withCrumbParents } from '../../utilities/auth';
import { ANALYTICS_RULES, DASHBOARD_RULES, DATA_INPUT_RULES, EVERYONE, GOVERNANCE_RULES, access, param } from '../../access';
import withPostHogTracker from '../../composeRouter';

const Profile = withLazyLoading(() => import('../../pages/Profile'));
const AboutPage = withLazyLoading(() => import('../../pages/AboutPage'));
const DataCorrection = withLazyLoading(
    () => import('../../components/DataCorrection/DataCorrection')
);
const Triggers = withLazyLoading(
    () => import('../../components/Triggers/TriggerListPage/TriggerListpage')
);
const CreateEditTrigger = withLazyLoading(
    () => import('../../components/Triggers/CreateEditTrigger/CreateEditTrigger')
);
const SystemPerformance = withLazyLoading(
    () => import("../../components/SystemPerformance/components/SystemPerformanceView"),
)

const miscRoutes = (queryClient: QueryClient) => [
    {
        path: ACCOUNT_ROUTES.root,
        element: <Navigate to={ACCOUNT_ROUTES.contact} replace />,
        handle: access(EVERYONE),
    },
    {
        path: ACCOUNT_ROUTES.pictureAndDashboard,
        element: <Profile />,
        handle: {
            ...withCrumbParents(createCrumbHandle('Profile picture and dashboard'), [
                { label: 'Profile', pathname: ACCOUNT_ROUTES.contact },
            ]),
            ...access(EVERYONE),
        },
    },
    {
        path: 'aboutPage',
        element: <AboutPage />,
        handle: { ...createCrumbHandle('About'), ...access(EVERYONE) },
    },
    {
        path: '/Data-Correction',
        element: <DataCorrection />,
        handle: { ...createCrumbHandle('Data Correction'), ...access(DASHBOARD_RULES.correctData) },
    },
    {
        path: '/Triggers',
        handle: createCrumbHandle('Triggers'),
        children: [
            {
                index: true,
                element: withPostHogTracker(<Triggers />),
                handle: access(ANALYTICS_RULES.triggers),
            },
            {
                path: 'CreateEditTrigger/:id?',
                element: withPostHogTracker(<CreateEditTrigger />),
                handle: { ...createCrumbHandle('Create/Edit Trigger'), ...access(ANALYTICS_RULES.createTrigger) },
            },
        ]
    },
    {
        path: "/systemPerformance",
        element: <SystemPerformance />,
        handle: { ...createCrumbHandle('System Performance'), ...access(GOVERNANCE_RULES.systemPerformance) },
    },
    {
        path: "/logbookTemplates",
        element: <LogbookTemplates />,
        handle: { ...createCrumbHandle('Logbook Templates'), ...access(DATA_INPUT_RULES.logbookTemplates) },
        loader: logbookTemplateLoader(queryClient)
    },
    {
        path: "/logbooks/:plantId",
        handle: createCrumbHandle('Log Books'),
        children: [
            {
                index: true,
                element: withPostHogTracker(<LogbookList />),
                handle: access(DATA_INPUT_RULES.logbooks, param('plantId')),
                loader: logbookListLoader(queryClient),
            },
            {
                path: 'logbook-configuration/add',
                element: <LogbookConfigurationPage />,
                handle: { ...createCrumbHandle('Configure Logbook'), ...access(DATA_INPUT_RULES.createLogbook, param('plantId')) },
            },
            {
                path: 'logbook-configuration/:logbookAssetConfigurationId',
                element: <LogbookConfigurationPage />,
                handle: { ...createCrumbHandle('Configure Logbook'), ...access(DATA_INPUT_RULES.createLogbook, param('plantId')) },
                loader: logbookConfigurationLoader(queryClient),
            },
        ]
    },
    {
        path: 'logbook-data-input',
        element: withPostHogTracker(<OcrDataInputPage />),
        handle: { ...createCrumbHandle('Input Logbook Data'), ...access(DATA_INPUT_RULES.logbookEntry) },
    },
];

export default miscRoutes;
